/**
 * POI list rendered below the map on PropertyLanding.
 *
 * Reads:
 *   • `pois` — the raw poi_list array from the property document
 *   • `translations` — map `{poi_id: translated_name}` from
 *     `translations[lang].pois` (returned by the backend translate endpoint)
 *   • `lang` — current landing language (`it`|`en`|`fr`|`es`|`de`)
 */

import { motion } from "framer-motion";
import * as LucideIcons from "lucide-react";
import { POI_LABELS, getPoiIcon, resolvePoiIconComponent } from "../data/poiIcons";

const formatDistance = (m, labels) => {
  if (m === null || m === undefined || Number.isNaN(m)) return null;
  const n = Number(m);
  if (!Number.isFinite(n) || n < 0) return null;
  if (n < 1000) return `${Math.round(n)} ${labels.meters}`;
  return `${(n / 1000).toFixed(n < 10000 ? 1 : 0)} ${labels.km}`;
};

const PropertyPOIList = ({ pois, translations = {}, lang = "it", theme }) => {
  if (!Array.isArray(pois) || pois.length === 0) return null;
  const labels = POI_LABELS[lang] || POI_LABELS.it;
  const sub = theme?.sub || "#71717a";

  return (
    <motion.section
      initial={{ opacity: 0, y: 12 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: "-40px" }}
      transition={{ duration: 0.4 }}
      className="mt-4 md:mt-5"
      data-testid="poi-list"
    >
      <div className="flex items-center gap-2 mb-2 md:mb-3">
        <span className="w-5 h-5 md:w-6 md:h-6 rounded-md md:rounded-lg flex items-center justify-center" style={{ background: `${theme?.text || "#0F172A"}0d` }}>
          <LucideIcons.MapPin className="w-3 h-3 md:w-3.5 md:h-3.5" style={{ color: theme?.text || "#0F172A" }} strokeWidth={2.3} />
        </span>
        <p
          className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.18em]"
          style={{ color: sub }}
        >
          {labels.section}
          <span className="mx-1.5 opacity-40">·</span>
          <span style={{ color: theme?.text || "#0F172A" }}>{pois.length}</span> {pois.length === 1 ? "luogo" : "luoghi"}
        </p>
      </div>
      <ul className="divide-y divide-zinc-100 md:divide-y-0 md:grid md:grid-cols-2 md:gap-x-8 md:gap-y-0.5">
        {pois.map((poi, idx) => {
          const meta = getPoiIcon(poi.icon);
          const Icon = resolvePoiIconComponent(poi.icon, LucideIcons);
          const isCustom = !!meta.custom;
          const name = translations[poi.id] || poi.name;
          if (!name) return null;
          const dist = formatDistance(poi.distance_m, labels);
          const walk = Number.isFinite(Number(poi.walk_min)) && Number(poi.walk_min) > 0 ? `${Math.round(Number(poi.walk_min))} ${labels.min}` : null;
          const drive = Number.isFinite(Number(poi.drive_min)) && Number(poi.drive_min) > 0 ? `${Math.round(Number(poi.drive_min))} ${labels.min}` : null;

          return (
            <motion.li
              key={poi.id || idx}
              initial={{ opacity: 0, x: -6 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.22, delay: idx * 0.02 }}
              className="flex items-center gap-2.5 md:gap-3 py-2 md:py-2 md:px-2.5 md:rounded-xl md:hover:bg-zinc-50 transition-colors"
              data-testid={`poi-item-${idx}`}
            >
              {isCustom ? (
                /* Custom icons (Metro M rosso) — brand di per sé, nessun wrapper colorato */
                <span className="flex-shrink-0 w-6 h-6 md:w-9 md:h-9 flex items-center justify-center">
                  <Icon className="w-5 h-5 md:w-8 md:h-8" />
                </span>
              ) : (
                <span
                  className="flex-shrink-0 w-6 h-6 md:w-9 md:h-9 rounded-md md:rounded-xl flex items-center justify-center"
                  style={{ background: `${meta.color}15` }}
                >
                  <Icon className="w-3 h-3 md:w-4 md:h-4" style={{ color: meta.color }} strokeWidth={2} />
                </span>
              )}
              <span className="flex-1 min-w-0 text-[13px] md:text-sm font-bold text-zinc-900 truncate leading-tight">
                {name}
              </span>
              <span className="flex items-center gap-1.5 md:gap-2 text-[10px] md:text-xs font-semibold text-zinc-500 whitespace-nowrap tabular-nums">
                {dist && <span>{dist}</span>}
                {walk && (
                  <span className="inline-flex items-center gap-0.5 md:gap-1">
                    <LucideIcons.Footprints className="w-3 h-3" strokeWidth={2.2} />
                    {walk}
                  </span>
                )}
                {drive && (
                  <span className="inline-flex items-center gap-0.5 md:gap-1">
                    <LucideIcons.Car className="w-3 h-3" strokeWidth={2.2} />
                    {drive}
                  </span>
                )}
              </span>
            </motion.li>
          );
        })}
      </ul>
    </motion.section>
  );
};

export default PropertyPOIList;
